import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ContractSectionSelector } from './ContractSectionSelector';
import { ContractTemplateLibrary } from './ContractTemplateLibrary';
import { ContractData, ContractSection } from '@/types/contract.types';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { BookTemplate, FileText, PenTool } from 'lucide-react';

const contractFormSchema = z.object({
  organization_name: z.string().min(1, "Organization name is required"),
  client_name: z.string().min(1, "Client name is required"),
  client_title: z.string().min(1, "Client title is required"),
  start_date: z.string().min(1, "Start date is required"),
  contract_value: z.string().min(1, "Contract value is required"),
  billing_contact: z.string().min(1, "Billing contact is required"),
  billing_email: z.string().email("Must be a valid email"),
  billing_phone: z.string().min(1, "Billing phone is required"),
  // Enhanced fields
  project_name: z.string().optional(),
  term_length: z.string().optional(),
  deliverables: z.string().optional(),
  consultant_name: z.string().optional(),
  consultant_title: z.string().optional(),
  payment_schedule: z.string().optional(),
});

type ContractFormValues = z.infer<typeof contractFormSchema>;

interface ContractGeneratorFormProps {
  msaSections: ContractSection[];
  sowSections: ContractSection[];
  onSubmit: (data: ContractFormValues, msaSections: string[], sowSections: string[]) => void;
  defaultValues?: Partial<ContractFormValues>;
}

export function ContractGeneratorForm({
  msaSections,
  sowSections,
  onSubmit,
  defaultValues
}: ContractGeneratorFormProps) {
  const [selectedMSASections, setSelectedMSASections] = useState<string[]>(
    msaSections.filter(s => s.isRequired).map(s => s.id)
  );
  const [selectedSOWSections, setSelectedSOWSections] = useState<string[]>(
    sowSections.filter(s => s.isRequired).map(s => s.id)
  );

  const form = useForm<ContractFormValues>({
    resolver: zodResolver(contractFormSchema),
    defaultValues: defaultValues || {
      organization_name: "",
      client_name: "",
      client_title: "",
      start_date: new Date().toISOString().split('T')[0],
      contract_value: "",
      billing_contact: "",
      billing_email: "",
      billing_phone: "",
      project_name: "",
      term_length: "3 month",
      deliverables: "",
      consultant_name: "Pablo Henderson",
      consultant_title: "President & CMO",
      payment_schedule: "",
    }
  });

  const handleSubmit = (values: ContractFormValues) => {
    onSubmit(values, selectedMSASections, selectedSOWSections);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Contract Information</CardTitle>
            <CardDescription>
              Enter the essential details for the contract. These will be used in both MSA and SOW documents.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="organization_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="client_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="contract_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contract Value</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="term_length"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Term Length</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 3 month" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="billing_contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Contact</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="billing_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="billing_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Phone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="consultant_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Consultant Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="consultant_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Consultant Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="advanced">
            <AccordionTrigger>Advanced SOW Details</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-4">
                <FormField
                  control={form.control}
                  name="project_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g. Social Media Strategy" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliverables"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deliverables (Markdown format)</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          placeholder="## Social Media Advertising Strategy
* Account performance audit
* Industry research and competitive analysis
* Media mix & budget allocation"
                          className="min-h-[200px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payment_schedule"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Schedule (Markdown format)</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          placeholder="* 50% due upon signing
* 25% due upon delivery of first milestone
* 25% due upon project completion"
                          className="min-h-[120px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Sections
            </CardTitle>
            <CardDescription>
              Customize which sections to include in your contracts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="templates">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="templates" className="gap-2">
                  <BookTemplate className="h-4 w-4" />
                  Templates
                </TabsTrigger>
                <TabsTrigger value="msa" className="gap-2">
                  <FileText className="h-4 w-4" />
                  MSA
                </TabsTrigger>
                <TabsTrigger value="sow" className="gap-2">
                  <PenTool className="h-4 w-4" />
                  SOW
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="templates" className="mt-4">
                <ContractTemplateLibrary
                  msaSections={msaSections}
                  sowSections={sowSections}
                  onSelectTemplate={(type, sections) => {
                    if (type === 'msa') {
                      setSelectedMSASections(sections);
                    } else {
                      setSelectedSOWSections(sections);
                    }
                  }}
                />
              </TabsContent>
              
              <TabsContent value="msa" className="mt-4">
                <ContractSectionSelector
                  sections={msaSections}
                  selectedSections={selectedMSASections}
                  onSectionToggle={(id) => {
                    setSelectedMSASections(prev => 
                      prev.includes(id) 
                        ? prev.filter(s => s !== id)
                        : [...prev, id]
                    );
                  }}
                />
              </TabsContent>
              
              <TabsContent value="sow" className="mt-4">
                <ContractSectionSelector
                  sections={sowSections}
                  selectedSections={selectedSOWSections}
                  onSectionToggle={(id) => {
                    setSelectedSOWSections(prev => 
                      prev.includes(id) 
                        ? prev.filter(s => s !== id)
                        : [...prev, id]
                    );
                  }}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button type="submit">
            Generate Contracts
          </Button>
        </div>
      </form>
    </Form>
  );
}